// src/pages/SettingsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { creditsApi, subscriptionApi, paymentApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreditBalance from '../components/CreditBalance';
import { trackSubscriptionUpgraded, trackCreditPackPurchased } from '../utils/analytics';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import PricingCard from '../components/Pricing/PricingCard';
import CreditPackCard from '../components/Pricing/CreditPackCard';

type PaymentStatus = 'idle' | 'verifying' | 'success' | 'failed' | 'expired' | 'pending';

const SettingsPage: React.FC = () => {
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'account');

  // Credits data
  const [balance, setBalance] = useState<number>(0);
  const [breakdown, setBreakdown] = useState<{ main: number; topup: number; bonus: number; earned: number } | null>(null);
  const [usageStats, setUsageStats] = useState<any>(null);

  // Session usage data
  const [sessionUsage, setSessionUsage] = useState<any[]>([]);
  const [sessionUsageHasMore, setSessionUsageHasMore] = useState(false);
  const [sessionUsageOffset, setSessionUsageOffset] = useState(0);
  const [sessionUsageLoading, setSessionUsageLoading] = useState(false);

  // Subscription data
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Token state
  const [hasToken, setHasToken] = useState(false);

  // Payment / geo state
  const [geoInfo, setGeoInfo] = useState<{ country_code: string; gateway: string; currency: string } | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState<string | null>(null); // Track which tier is processing
  const [billingToggle, setBillingToggle] = useState<'monthly' | 'yearly'>('monthly');
  const GCC_COUNTRIES = ['AE', 'SA', 'OM', 'BH', 'KW', 'QA'];
  const isGCC = geoInfo ? GCC_COUNTRIES.includes(geoInfo.country_code) : false;

  // GCC-specific pricing overrides
  const GCC_PRICES: Record<string, { monthly: number; yearly: number }> = {
    pro: { monthly: 15, yearly: 135 },
    ultimate: { monthly: 30, yearly: 270 },
  };
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<string | null>(null); // 'credits' or 'success'

  // Profile edit state
  const [editPhone, setEditPhone] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Monitor token availability (same pattern as CreditBalance)
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('auth_token');
      if (token && !hasToken) {
        setHasToken(true);
      }
    };

    checkToken();
    const interval = setInterval(checkToken, 500);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!hasToken) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [hasToken]);

  const fetchSessionUsage = useCallback(async (offset: number, append: boolean = false) => {
    setSessionUsageLoading(true);
    try {
      const result = await creditsApi.getSessionUsage(20, offset);
      if (append) {
        setSessionUsage(prev => [...prev, ...result.sessions]);
      } else {
        setSessionUsage(result.sessions);
      }
      setSessionUsageHasMore(result.has_more);
      setSessionUsageOffset(offset);
    } catch (error) {
      console.error('[SETTINGS] Failed to fetch session usage:', error);
    } finally {
      setSessionUsageLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [balanceResult, statsResult, subInfoResult, tiersResult, sessionResult] = await Promise.allSettled([
        creditsApi.getBalance(),
        creditsApi.getUsageStats(),
        subscriptionApi.getInfo(),
        subscriptionApi.getTiers(),
        creditsApi.getSessionUsage(20, 0)
      ]);

      if (balanceResult.status === 'fulfilled') {
        setBalance(balanceResult.value.credit_balance);
        setBreakdown(balanceResult.value.breakdown || null);
      }

      if (statsResult.status === 'fulfilled') {
        setUsageStats(statsResult.value);
      }

      if (subInfoResult.status === 'fulfilled') {
        setSubscriptionInfo(subInfoResult.value);
        setEditPhone(subInfoResult.value.phone_number || '');
        setEditLocation(subInfoResult.value.location || '');
      }

      if (tiersResult.status === 'fulfilled') {
        setTiers(tiersResult.value);
      }

      if (sessionResult.status === 'fulfilled') {
        setSessionUsage(sessionResult.value.sessions);
        setSessionUsageHasMore(sessionResult.value.has_more);
      }
    } catch (error) {
      console.error('Failed to fetch settings data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch geo info once on mount
  useEffect(() => {
    paymentApi.detectGeo()
      .then(setGeoInfo)
      .catch(() => setGeoInfo({ country_code: 'IN', gateway: 'razorpay', currency: 'INR' }));
  }, []);

  // Only fetch authenticated data once token is available
  useEffect(() => {
    if (hasToken) {
      fetchData();
    }
  }, [hasToken, fetchData]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Handle redirect-back from payment gateway
  useEffect(() => {
    const orderId = searchParams.get('order_id');
    const status = searchParams.get('payment_status');
    const razorpayPaymentId = searchParams.get('razorpay_payment_id');
    const paymentParam = searchParams.get('payment');
    // Accept redirect if we have order_id + (payment_status OR razorpay_payment_id)
    if (!orderId || (!status && !razorpayPaymentId)) return;
    // Capture payment type before params get cleared
    if (paymentParam) setPaymentType(paymentParam);

    setPaymentStatus('verifying');
    setPaymentOrderId(orderId);

    let attempts = 0;
    const maxAttempts = 10;
    const interval = 2000;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await paymentApi.verifyOrder(orderId, razorpayPaymentId || undefined);
        if (cancelled) return;
        if (result.status === 'completed') {
          setPaymentStatus('success');
          fetchData();
          setSearchParams({ tab: 'subscription' });
          return;
        }
        if (result.status === 'failed') {
          setPaymentStatus('failed');
          setSearchParams({ tab: 'subscription' });
          return;
        }
        if (result.status === 'expired') {
          setPaymentStatus('expired');
          setSearchParams({ tab: 'subscription' });
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          setPaymentStatus('pending');
          setSearchParams({ tab: 'subscription' });
        }
      } catch {
        if (!cancelled) {
          setPaymentStatus('failed');
          setSearchParams({ tab: 'subscription' });
        }
      }
    };
    poll();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const changeTab = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleSubscribe = async (tierCode: string, tierPriceInr: number, tierPriceUsd: number) => {
    const isFree = tierPriceInr <= 0 && tierPriceUsd <= 0;

    if (isFree) {
      if (!window.confirm(`Subscribe to ${tierCode}?`)) return;
      try {
        await subscriptionApi.subscribe(tierCode);
        window.alert('Subscribed successfully!');
        fetchData();
      } catch (error: any) {
        window.alert(`Failed to subscribe: ${error.response?.data?.detail || error.message}`);
      }
      return;
    }

    const gateway = geoInfo?.gateway || 'stripe';
    const cleanTierCode = tierCode.replace(':yearly', '').replace(':monthly', '');

    // Stripe trial user starting their own plan early (e.g. credits ran out
    // before day 7): end the trial now — immediate charge + fresh monthly
    // credits — instead of creating a second subscription via checkout.
    if (
      subscriptionInfo?.subscription_status === 'trialing' &&
      subscriptionInfo?.payment_gateway === 'stripe' &&
      cleanTierCode === subscriptionInfo?.subscription_tier
    ) {
      if (!window.confirm(
        `Start your paid ${subscriptionInfo?.tier_name || cleanTierCode} plan now? ` +
        `Your card will be charged immediately and your credits reset to the full monthly amount.`
      )) return;
      setPaymentProcessing(tierCode);
      try {
        await paymentApi.convertTrialNow();
        // The Stripe webhook flips the subscription and resets credits — poll
        // until the change lands (usually a few seconds).
        let converted = false;
        for (let i = 0; i < 10; i++) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          try {
            const info = await subscriptionApi.getInfo();
            if (info?.subscription_status === 'active') {
              converted = true;
              break;
            }
          } catch { /* keep polling */ }
        }
        setPaymentType('success');
        setPaymentStatus(converted ? 'success' : 'pending');
        fetchData();
      } catch (error: any) {
        window.alert(`Failed to start your plan: ${error.response?.data?.detail || error.message}`);
      } finally {
        setPaymentProcessing(null);
      }
      return;
    }

    setPaymentProcessing(tierCode); // Set the specific tier being processed
    try {
      const countryCode = geoInfo?.country_code || undefined;
      const billingInterval = tierCode.includes(':yearly') ? 'yearly' : 'monthly';
      const order = await paymentApi.createOrder(cleanTierCode, gateway, countryCode, billingInterval);
      console.log('[PAYMENT] Order response:', JSON.stringify(order));

      // Razorpay popup flow
      if (order.payment_mode === 'popup' && order.razorpay_key_id) {
        openRazorpayPopup(order);
        return;
      }

      // Stripe / fallback redirect flow
      window.location.href = order.redirect_url;
    } catch (error: any) {
      window.alert(`Failed to create payment order: ${error.response?.data?.detail || error.message}`);
      setPaymentProcessing(null); // Clear processing state on error
    }
  };

  const openRazorpayPopup = (order: any) => {
    const isSubscription = !!order.razorpay_subscription_id;
    const isCreditPack = !isSubscription && !!order.razorpay_order_id;

    const options: any = {
      key: order.razorpay_key_id,
      name: 'CorestratAI  Solutions',
      image: `${window.location.origin}/corestrat-logo.png`,
      description: isCreditPack
        ? `Credit Pack — ${order.currency === 'INR' ? '₹' : '$'}${order.amount}`
        : order.amount <= 199
          ? `7-day free trial • ${order.currency === 'INR' ? '₹' : '$'}5 auth charge (refundable) • Then ${order.currency === 'INR' ? '₹' : '$'}${order.amount}/month after 7 days`
          : `${order.currency === 'INR' ? '₹' : '$'}${order.amount}/month subscription`,
      handler: async (response: any) => {
        // Payment successful — verify and activate
        console.log('[PAYMENT] Razorpay handler response:', JSON.stringify(response));
        try {
          const razorpayPaymentId = response.razorpay_payment_id || '';
          const razorpaySubId = response.razorpay_subscription_id || '';
          const result = await paymentApi.verifyOrder(order.order_id, razorpayPaymentId, razorpaySubId);
          if (result.status === 'completed') {
            if (isCreditPack) {
              trackCreditPackPurchased(1, order.amount);
            } else {
              trackSubscriptionUpgraded(result.tier_code, order.gateway);
            }
            setPaymentType(isCreditPack ? 'credits' : 'success');
            setPaymentStatus('success');
            fetchData();
          } else {
            setPaymentStatus('pending');
          }
        } catch {
          setPaymentStatus('failed');
        }
        setPaymentProcessing(null);
      },
      modal: {
        ondismiss: () => {
          setPaymentProcessing(null);
        },
      },
      theme: {
        color: '#7c3aed',
      },
    };

    // Subscription: pass subscription_id + force popup (not redirect)
    if (isSubscription) {
      options.subscription_id = order.razorpay_subscription_id;
      options.redirect = false;
    }

    // Credit pack: pass order_id + amount
    if (isCreditPack) {
      options.order_id = order.razorpay_order_id;
      options.amount = order.amount * 100; // paise
      options.currency = order.currency;
    }

    // Load Razorpay script if not already loaded
    if ((window as any).Razorpay) {
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } else {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const rzp = new (window as any).Razorpay(options);
        rzp.open();
      };
      script.onerror = () => {
        window.alert('Failed to load payment gateway. Please try again.');
        setPaymentProcessing(null);
      };
      document.body.appendChild(script);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      await subscriptionApi.updateProfile({
        phone_number: editPhone,
        location: editLocation,
      });
      setProfileMessage({ type: 'success', text: 'Profile updated' });
      fetchData();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to update profile';
      setProfileMessage({ type: 'error', text: msg });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMessage(null);
    try {
      const result = await creditsApi.redeemPromo(promoCode.trim());
      setPromoMessage({ type: 'success', text: result.message });
      setPromoCode('');
      fetchData();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to redeem promo code';
      setPromoMessage({ type: 'error', text: msg });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel? You\'ll keep access until the end of your current billing period.')) {
      return;
    }

    try {
      const result = await subscriptionApi.cancel();
      window.alert(result.message);
      fetchData(); // Refresh user data
    } catch (error: any) {
      window.alert(`Failed to cancel: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const result = await subscriptionApi.reactivate();
      window.alert(result.message);
      fetchData(); // Refresh user data
    } catch (error: any) {
      window.alert(`Failed to reactivate: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleLoadMoreSessions = () => {
    fetchSessionUsage(sessionUsageOffset + 20, true);
  };

  const formatPrice = (tier: any): string => {
    if (!geoInfo) return `$${tier.price_usd}`;
    if (geoInfo.currency === 'INR') {
      return `\u20B9${tier.price_inr}`;
    }
    return `$${tier.price_usd}`;
  };

  const getGatewayLabel = (): string => {
    if (!geoInfo) return '';
    return geoInfo.gateway === 'razorpay' ? 'via Razorpay' : 'via Stripe';
  };

  const formatSessionDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Clean up raw session titles for display
  const formatSessionTitle = (raw: string): string => {
    let title = raw;
    // Strip **Attachment: filename patterns
    title = title.replace(/\s*\*\*Attachment:\s*\S*/gi, '').trim();
    // Strip leading "with the attached data, " or "with the attached data " prefix
    title = title.replace(/^with the attached data\s*,?\s*/i, '');
    // Capitalize first letter
    if (title.length > 0) {
      title = title.charAt(0).toUpperCase() + title.slice(1);
    }
    // Truncate long titles
    if (title.length > 80) {
      title = title.slice(0, 77) + '...';
    }
    return title || 'Conversation';
  };

  // Action type color dots for usage breakdown
  const actionColors: Record<string, string> = {
    chat: 'bg-blue-500',
    code_execution: 'bg-emerald-500',
    codebook: 'bg-purple-500',
    slide: 'bg-amber-500',
    visualization: 'bg-pink-500',
    unknown: 'bg-gray-400',
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* Navigation Header */}
      <header className="flex-shrink-0 bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.history.back()}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back</span>
              </button>
              <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs text-muted-foreground">
                support@cubemate.ai
              </span>
              <a
                href="/codemate"
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Home
              </a>
              <CreditBalance />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />

        <div className="flex-1 bg-background overflow-auto">
          <div className="px-[10px] py-6">
            {/* Subtitle */}
            <div className="mb-4">
              <p className="text-muted-foreground">
                Manage your account, credits, and subscription
              </p>
            </div>

            {/* Tabs */}
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden">
              <div className="border-b border-border/50">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => changeTab('account')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'account'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    Account
                  </button>
                  <button
                    onClick={() => changeTab('credits')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'credits'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    Credits & Usage
                  </button>
                  <button
                    onClick={() => changeTab('subscription')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'subscription'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    Subscription
                  </button>
                  <button
                    onClick={() => { logout(); window.location.href = '/login'; }}
                    className="px-6 py-4 text-sm font-medium border-b-2 border-transparent text-red-500 hover:text-red-600 hover:border-red-300 transition-colors ml-auto"
                  >
                    Sign Out
                  </button>
                </nav>
              </div>

              <div className="p-5">
                {/* ================================================================
                    ACCOUNT TAB
                    ================================================================ */}
                {activeTab === 'account' && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">
                      Account Information
                    </h2>

                    {/* Read-only fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</p>
                        <p className="mt-1 text-foreground font-medium truncate">{subscriptionInfo?.display_name || '—'}</p>
                      </div>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</p>
                        <p className="mt-1 text-foreground font-medium truncate">{subscriptionInfo?.email || '—'}</p>
                      </div>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Current Plan</p>
                        <p className="mt-1 text-foreground font-semibold">{subscriptionInfo?.tier_name || '—'}</p>
                      </div>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                        <div className="mt-1">
                          {(() => {
                            // v3.19.7.1: human-friendly status labels (was raw enum like "trial_active")
                            const status = subscriptionInfo?.subscription_status;
                            const label = status === 'active' ? 'Active'
                              : status === 'trial_active' ? 'Free Trial'
                              : status === 'trial_expired' ? 'Trial Ended'
                              : status === 'cancelled' ? 'Cancelled'
                              : status === 'payment_failed' ? 'Payment Failed'
                              : status === 'pending_payment' ? 'Awaiting Payment'
                              : '—';
                            const isPositive = status === 'active' || status === 'trial_active';
                            return (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                isPositive
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isPositive ? 'bg-emerald-500' : 'bg-yellow-500'}`}></span>
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Credits Remaining</p>
                        <p className="mt-1 text-foreground font-semibold">{balance.toLocaleString()}</p>
                      </div>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Star Tier</p>
                        <p className="mt-1 text-foreground font-semibold">{subscriptionInfo?.star?.tier_name || 'New'}</p>
                      </div>
                    </div>

                    {/* Editable fields */}
                    <div className="mt-6 pt-4 border-t border-border/50">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Edit Profile</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Phone Number</label>
                          <input
                            type="tel"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            placeholder="e.g. +91 98765 43210"
                            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Location</label>
                          <input
                            type="text"
                            value={editLocation}
                            onChange={(e) => setEditLocation(e.target.value)}
                            placeholder="e.g. Chennai, India"
                            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleSaveProfile}
                            disabled={profileSaving}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                          >
                            {profileSaving ? 'Saving...' : 'Save'}
                          </button>
                          {profileMessage && (
                            <span className={`text-sm ${profileMessage.type === 'success' ? 'text-emerald-500' : 'text-red-500'}`}>
                              {profileMessage.text}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-muted-foreground">
                      Need help? Contact us at <a href="mailto:support@cubemate.ai" className="text-primary hover:underline font-medium">support@cubemate.ai</a>
                    </div>
                  </div>
                )}

                {/* ================================================================
                    CREDITS & USAGE TAB
                    ================================================================ */}
                {activeTab === 'credits' && (
                  <div className="space-y-4">
                    {/* Compact Balance Card */}
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-4 text-white">
                      <h3 className="text-sm font-medium opacity-90">Current Balance</h3>
                      <p className="text-3xl font-bold mt-1">{balance.toLocaleString()} credits</p>
                    </div>

                    {/* This Month Stats — compact card with inline action breakdown */}
                    {usageStats && (
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-foreground">This Month</h3>
                          <span className="text-lg font-bold text-foreground">
                            {usageStats.total_credits_used} credits used
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(usageStats.credits_by_action || {}).map(([action, credits]: [string, any]) => (
                            <span
                              key={action}
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                            >
                              <span className={`w-2 h-2 rounded-full mr-1.5 ${actionColors[action] || actionColors.unknown}`}></span>
                              {action.replace('_', ' ')} — {credits}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Session Usage List */}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">Session History</h3>
                      <div className="space-y-2">
                        {sessionUsage.length === 0 && !sessionUsageLoading && (
                          <p className="text-sm text-muted-foreground py-4 text-center">No session usage yet</p>
                        )}
                        {sessionUsage.map((session) => (
                          <div
                            key={session.session_id}
                            className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-foreground font-medium truncate">
                                  {formatSessionTitle(session.title)}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-xs text-muted-foreground">
                                    {formatSessionDate(session.date)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    · {session.message_count} message{session.message_count !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                <span className="text-sm font-semibold text-red-500 dark:text-red-400">
                                  -{session.credits_used}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Load More */}
                      {sessionUsageHasMore && (
                        <div className="text-center mt-3">
                          <button
                            onClick={handleLoadMoreSessions}
                            disabled={sessionUsageLoading}
                            className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground transition-colors"
                          >
                            {sessionUsageLoading ? 'Loading...' : 'Load More'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ================================================================
                    SUBSCRIPTION TAB
                    ================================================================ */}
                {activeTab === 'subscription' && (
                  <div className="space-y-4">
                    {/* Payment Status Banner */}
                    {paymentStatus === 'verifying' && (
                      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <p className="text-sm text-blue-700 dark:text-blue-300">Verifying your payment...</p>
                      </div>
                    )}
                    {paymentStatus === 'success' && (
                      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                          {paymentType === 'credits'
                            ? 'Top-up successful! Your credits have been added.'
                            : 'Payment successful! Your subscription is now active.'}
                        </p>
                      </div>
                    )}
                    {paymentStatus === 'failed' && (
                      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                          Payment failed. Please try again or contact support.
                        </p>
                      </div>
                    )}
                    {paymentStatus === 'expired' && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">
                          Payment session expired. Please try subscribing again.
                        </p>
                      </div>
                    )}
                    {paymentStatus === 'pending' && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">
                          Payment is being processed. Your subscription will activate shortly.
                          {paymentOrderId && <span className="text-xs ml-2">(Order: {paymentOrderId})</span>}
                        </p>
                      </div>
                    )}

                    {/* Current Subscription */}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">
                        Current Subscription
                      </h3>
                      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-bold text-foreground">
                              {subscriptionInfo?.tier_name}
                            </h4>
                            <p className="text-muted-foreground text-sm mt-1">
                              {subscriptionInfo?.credits_per_month} credits per month
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Status: <span className="font-medium">{subscriptionInfo?.subscription_status}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-foreground">
                              {balance}
                            </p>
                            <p className="text-xs text-muted-foreground mb-2">credits remaining</p>
                            {breakdown && (
                              <div className="text-xs text-muted-foreground space-y-0.5 border-t border-border pt-2">
                                <div className="flex justify-between gap-4">
                                  <span>Plan</span>
                                  <span className="font-medium text-foreground">{breakdown.main}</span>
                                </div>
                                {breakdown.topup > 0 && (
                                  <div className="flex justify-between gap-4">
                                    <span>Top-up</span>
                                    <span className="font-medium text-blue-500">{breakdown.topup}</span>
                                  </div>
                                )}
                                {breakdown.bonus > 0 && (
                                  <div className="flex justify-between gap-4">
                                    <span>Bonus</span>
                                    <span className="font-medium text-green-500">{breakdown.bonus}</span>
                                  </div>
                                )}
                                {breakdown.earned > 0 && (
                                  <div className="flex justify-between gap-4">
                                    <span>Earned</span>
                                    <span className="font-medium text-amber-500">{breakdown.earned}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Billing Info — removed "Next billing" and "Billed via" since auto-renew line below covers it */}

                        {/* Payment Failed Banner */}
                        {subscriptionInfo?.subscription_status === 'payment_failed' && (
                          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <p className="text-sm text-red-900 dark:text-red-200">
                              <strong className="font-semibold">Payment Failed</strong>
                              {' '}— Your last payment could not be processed. Please update your payment method to continue using {subscriptionInfo?.subscription_tier} features.
                            </p>
                          </div>
                        )}

                        {/* Cancel/Reactivate Section
                            v3.19.7.6: gate on has_subscribed instead of tier!=='basic'.
                            Trial users (tier='starter', has_subscribed=false) were falling
                            into the else-branch and seeing an alarming "Subscription Cancelled
                            — Reactivate Subscription" banner despite never having subscribed.
                            Now this whole section only renders for users who have ACTUALLY
                            paid for a subscription (has_subscribed=true). */}
                        {subscriptionInfo?.has_subscribed && (
                          <div className="mt-6 pt-6 border-t border-border">
                            {subscriptionInfo?.auto_renew ? (
                              <div>
                                <p className="text-sm text-muted-foreground mb-3">
                                  Your subscription will automatically renew
                                  {subscriptionInfo?.subscription_end_date && (
                                    <> on <span className="font-semibold text-foreground">
                                      {new Date(subscriptionInfo.subscription_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </span></>
                                  )}
                                </p>
                                <button
                                  onClick={handleCancelSubscription}
                                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                  <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Cancel Subscription
                                  </span>
                                </button>
                              </div>
                            ) : (
                              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                <p className="text-sm text-amber-900 dark:text-amber-200 mb-3">
                                  <strong className="font-semibold">Subscription Cancelled</strong>
                                  {' '}— You'll keep access until
                                  {subscriptionInfo?.subscription_end_date && (
                                    <> <span className="font-semibold">
                                      {new Date(subscriptionInfo.subscription_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </span></>
                                  )}
                                </p>
                                <button
                                  onClick={handleReactivateSubscription}
                                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Reactivate Subscription
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* LTD code entry point — wording stays generic ("deal code",
                          not "lifetime deal") so regular subscribers don't leave
                          the page to go hunting for a deal that isn't for them */}
                      <p className="text-xs text-muted-foreground mt-3">
                        Have a deal code?{' '}
                        <Link to="/redeem" className="text-purple-500 hover:text-purple-600 underline font-medium">
                          Redeem it
                        </Link>
                      </p>
                    </div>

                    {/* Available Tiers */}
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3">
                        Available Plans
                      </h3>
                      {geoInfo && (
                        <p className="text-xs text-muted-foreground mb-4">
                          Prices shown in {geoInfo.currency === 'INR' ? 'Indian Rupees (₹)' : 'US Dollars ($)'} • Payment via {geoInfo.gateway === 'razorpay' ? 'Razorpay' : 'Stripe'}
                          {isGCC && ' • Save up to 30% with yearly plans'}
                        </p>
                      )}
                      {isGCC && (
                        <div className="flex items-center justify-center gap-3 mb-6">
                          <span className={`text-sm ${billingToggle === 'monthly' ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>Monthly</span>
                          <button
                            onClick={() => setBillingToggle(billingToggle === 'monthly' ? 'yearly' : 'monthly')}
                            className={`relative w-12 h-6 rounded-full transition-colors ${billingToggle === 'yearly' ? 'bg-blue-500' : 'bg-gray-600'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${billingToggle === 'yearly' ? 'translate-x-6' : ''}`} />
                          </button>
                          <span className={`text-sm ${billingToggle === 'yearly' ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>Yearly <span className="text-green-500 text-xs">Save up to 30%</span></span>
                        </div>
                      )}
                      {(() => {
                        const visibleTiers = tiers
                          .filter((tier: any) => tier.tier_code !== 'team')
                          .filter((tier: any) => {
                            if (tier.tier_code.startsWith('credit_pack')) {
                              // Only show credit packs to Pro/Ultimate/lifetime-deal users
                              const st = subscriptionInfo?.subscription_tier || '';
                              if (st !== 'pro' && st !== 'ultimate' && !st.startsWith('ltd_')) return false;
                              // One pack worldwide since migration 025 (+100 for ₹99/$5);
                              // credit_pack_gcc retired
                              return tier.tier_code === 'credit_pack_200';
                            }
                            return true;
                          });
                        const gridCols = visibleTiers.length >= 4
                          ? 'lg:grid-cols-4'
                          : visibleTiers.length === 3
                          ? 'lg:grid-cols-3'
                          : 'lg:grid-cols-2';
                        return (
                      <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6 max-w-6xl mx-auto`}>
                        {visibleTiers
                          .map((tier: any) => {
                            // v3.19.7.6: default to INR before geo loads. India is phase 1 — most
                            // users are INR. This kills the "$ flashes briefly then flips to ₹"
                            // UX bug. Same fix as v3.19.7.1 applied to PricingSection.tsx — this
                            // is a parallel render path the earlier fix missed.
                            const currency = (geoInfo?.currency as 'INR' | 'USD') || 'INR';
                            const credits = currency === 'USD' && tier.credits_per_month_usd ? tier.credits_per_month_usd : tier.credits_per_month;

                            // Credit pack — render the dedicated card with quantity selector
                            if (tier.tier_code.startsWith('credit_pack')) {
                              return (
                                <CreditPackCard
                                  key={tier.id}
                                  tierCode={tier.tier_code}
                                  priceInr={tier.price_inr}
                                  priceUsd={tier.price_usd}
                                  creditsPerPack={credits}
                                  currency={currency}
                                  onPurchase={(qty) => handleSubscribe(
                                    `${tier.tier_code}:${qty}`,
                                    tier.price_inr * qty,
                                    tier.price_usd * qty
                                  )}
                                />
                              );
                            }

                            const isCurrent = tier.tier_code === subscriptionInfo?.subscription_tier;
                            const isUltimate = tier.tier_code === 'ultimate';
                            // A trialing Stripe user must be able to click their
                            // own tier to convert early — never show them a
                            // disabled "Current Plan" button.
                            const isTrialingCurrent = isCurrent
                              && subscriptionInfo?.subscription_status === 'trialing'
                              && subscriptionInfo?.payment_gateway === 'stripe';

                            let upgradeLabel = 'Get Started';
                            if (isTrialingCurrent) {
                              upgradeLabel = `Start ${tier.tier_name} now`;
                            } else if (isCurrent) {
                              upgradeLabel = 'Current Plan';
                            } else if (subscriptionInfo?.subscription_tier) {
                              upgradeLabel = `Upgrade to ${tier.tier_name}`;
                            }

                            // GCC price override
                            const gccPrice = isGCC ? GCC_PRICES[tier.tier_code] : null;
                            const displayPriceUsd = gccPrice
                              ? (billingToggle === 'yearly' ? gccPrice.yearly : gccPrice.monthly)
                              : tier.price_usd;
                            const billingLabel = isGCC && billingToggle === 'yearly'
                              ? `/year`
                              : undefined;
                            const savingsNote = isGCC && billingToggle === 'yearly' && gccPrice
                              ? `Save $${(gccPrice.monthly * 12) - gccPrice.yearly}/yr`
                              : undefined;

                            return (
                              <PricingCard
                                key={tier.id}
                                tierName={tier.tier_name}
                                tierCode={tier.tier_code}
                                priceInr={tier.price_inr}
                                priceUsd={displayPriceUsd}
                                creditsPerMonth={credits}
                                features={tier.features}
                                currency={currency}
                                isCurrentTier={isCurrent && !isTrialingCurrent}
                                isPopular={isUltimate}
                                isComingSoon={false}
                                onUpgrade={() => handleSubscribe(
                                  isGCC ? `${tier.tier_code}:${billingToggle}` : tier.tier_code,
                                  tier.price_inr,
                                  displayPriceUsd
                                )}
                                upgradeLabel={paymentProcessing === tier.tier_code ? 'Processing...' : upgradeLabel}
                                billingLabel={billingLabel}
                                savingsNote={savingsNote}
                              />
                            );
                          })}
                      </div>
                        );
                      })()}
                      <div className="mt-6 text-sm text-muted-foreground flex items-center justify-between">
                        <span>Questions about billing? Contact <a href="mailto:support@cubemate.ai" className="text-primary hover:underline font-medium">support@cubemate.ai</a></span>
                        <span>Cubemate AI &mdash; Powered by <a href="https://corestratai.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">corestratai.com</a></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
